import { ChangeDetectionStrategy, Component, computed, inject, Signal, signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { AppRoutes, AuthRoutes, ENV_CONFIG, EnvConfig, LegalRoutes, PLATFORM_TOKEN, PlatformProvider } from '@core';
import { Observable, take } from 'rxjs';
import { AuthWithAppleHandler } from '../../application/handlers/auth-with-apple.handler';
import { AuthWithGoogleHandler } from '../../application/handlers/auth-with-google.handler';
import { RegisterHandler } from '../../application/handlers/register.handler';
import { FirebaseAuthError } from '../../application/utils';
import { calculatePasswordStrength, PasswordStrength } from '../../application/utils/password-strength.util';

interface RegisterFormShape {
  firstName: FormControl<string>;
  lastName: FormControl<string>;
  email: FormControl<string>;
  password: FormControl<string>;
  terms: FormControl<boolean>;
}

@Component({
  selector: 'auth-register-page',
  templateUrl: './register.page.html',
  styleUrls: ['./register.page.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class RegisterPage {
  readonly loginPath: string = `/${AppRoutes.AUTH}/${AuthRoutes.LOGIN}`;
  readonly welcomePath: string = `/${AppRoutes.AUTH}/${AuthRoutes.WELCOME}`;
  readonly termsPath: string = `/${AppRoutes.LEGAL}/${LegalRoutes.TERMS}`;
  readonly privacyPath: string = `/${AppRoutes.LEGAL}/${LegalRoutes.PRIVACY}`;

  readonly loading: WritableSignal<boolean> = signal<boolean>(false);
  readonly errorMessage: WritableSignal<string | null> = signal<string | null>(null);

  readonly form: FormGroup<RegisterFormShape> = new FormGroup<RegisterFormShape>({
    firstName: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required, Validators.minLength(2)]
    }),
    lastName: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required, Validators.minLength(2)]
    }),
    email: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required, Validators.email]
    }),
    password: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required, Validators.minLength(8)]
    }),
    terms: new FormControl<boolean>(false, {
      nonNullable: true,
      validators: [Validators.requiredTrue]
    })
  });

  readonly passwordValue: Signal<string> = toSignal(
    this.form.controls.password.valueChanges.pipe(takeUntilDestroyed()),
    { initialValue: '' }
  );

  readonly passwordStrength: Signal<PasswordStrength> = computed(
    (): PasswordStrength => calculatePasswordStrength(this.passwordValue())
  );

  private readonly registerHandler: RegisterHandler = inject(RegisterHandler);
  private readonly authWithGoogleHandler: AuthWithGoogleHandler = inject(AuthWithGoogleHandler);
  private readonly authWithAppleHandler: AuthWithAppleHandler = inject(AuthWithAppleHandler);
  private readonly platform: PlatformProvider = inject(PLATFORM_TOKEN);
  private readonly env: EnvConfig = inject(ENV_CONFIG);
  private readonly router: Router = inject(Router);
  private readonly translate: TranslateService = inject(TranslateService);

  // Na local/stage zawsze (testy UI); na prod tylko dla natywnego iOS (App Store Guideline 4.8).
  readonly showAppleSignIn: boolean = !this.env.production || this.platform.isiOS;

  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const { firstName, lastName, email, password } = this.form.getRawValue();
    this.runRegister({ firstName, lastName, email, password });
  }

  onGoogleClick(): void {
    if (this.loading()) return;
    this.runProvider(this.authWithGoogleHandler.handle());
  }

  onAppleClick(): void {
    if (this.loading()) return;
    this.runProvider(this.authWithAppleHandler.handle());
  }

  private runProvider(stream: Observable<void>): void {
    this.loading.set(true);
    this.errorMessage.set(null);
    stream.pipe(take(1)).subscribe({
      complete: (): void => this.loading.set(false),
      error: (err: Error): void => {
        this.loading.set(false);
        this.errorMessage.set(err.message);
      }
    });
  }

  goBack(): void {
    void this.router.navigate([this.welcomePath]);
  }

  emailError(): string | null {
    const ctrl: FormControl<string> = this.form.controls.email;
    if (ctrl.errors?.['taken']) {
      return this.translate.instant('auth.errors.emailTaken');
    }
    if (ctrl.touched && ctrl.invalid) {
      return this.translate.instant('auth.errors.emailFormat');
    }
    return null;
  }

  firstNameError(): string | null {
    const ctrl: FormControl<string> = this.form.controls.firstName;
    if (ctrl.touched && ctrl.invalid) return this.translate.instant('auth.errors.nameMin');
    return null;
  }

  lastNameError(): string | null {
    const ctrl: FormControl<string> = this.form.controls.lastName;
    if (ctrl.touched && ctrl.invalid) return this.translate.instant('auth.errors.nameMin');
    return null;
  }

  passwordError(): string | null {
    const ctrl: FormControl<string> = this.form.controls.password;
    if (ctrl.touched && ctrl.invalid) return this.translate.instant('auth.errors.passwordMin');
    return null;
  }

  private runRegister(payload: { firstName: string; lastName: string; email: string; password: string }): void {
    this.loading.set(true);
    this.errorMessage.set(null);
    const obs: Observable<void> = this.registerHandler.handle(payload);
    obs.pipe(take(1)).subscribe({
      complete: (): void => this.loading.set(false),
      error: (err: Error): void => {
        this.loading.set(false);
        if (err instanceof FirebaseAuthError && err.code === 'auth/email-already-in-use') {
          this.form.controls.email.setErrors({ taken: true });
          this.form.controls.email.markAsTouched();
          return;
        }
        this.errorMessage.set(err.message);
      }
    });
  }
}
